import React from 'react';
import Icon from '../../../components/AppIcon';
import SettingsSection from './SettingsSection';
import SettingItem from './SettingItem';
import ToggleSwitch from './ToggleSwitch';

/**
 * Enhanced notifications tab component for employer profile settings
 * Uses main profile data and saves through central save function
 */
const EnhancedNotificationsTab = ({ profileData, onInputChange }) => {
  // Get notification settings from main profile data or use defaults
  const notificationSettings = {
    email: {
      newApplications: profileData?.notification_settings?.email?.newApplications ?? true,
      messages: profileData?.notification_settings?.email?.messages ?? true,
      candidateMatches: profileData?.notification_settings?.email?.candidateMatches ?? true,
      paymentUpdates: profileData?.notification_settings?.email?.paymentUpdates ?? true,
      projectUpdates: profileData?.notification_settings?.email?.projectUpdates ?? true,
      marketingEmails: profileData?.notification_settings?.email?.marketingEmails ?? false,
      securityAlerts: profileData?.notification_settings?.email?.securityAlerts ?? true,
      systemUpdates: profileData?.notification_settings?.email?.systemUpdates ?? false,
      contractReminders: profileData?.notification_settings?.email?.contractReminders ?? true,
      interviewReminders: profileData?.notification_settings?.email?.interviewReminders ?? true
    },
    push: {
      messages: profileData?.notification_settings?.push?.messages ?? true,
      newApplications: profileData?.notification_settings?.push?.newApplications ?? true,
      urgentUpdates: profileData?.notification_settings?.push?.urgentUpdates ?? true
    },
    frequency: {
      digestFrequency: profileData?.notification_settings?.frequency?.digestFrequency ?? 'daily'
    }
  };
  
  // Handle notification setting changes
  const handleToggleChange = (setting, value) => {
    // Get fresh notification settings from profileData
    const currentSettings = profileData.notification_settings || notificationSettings;
    let newSettings;
    
    // Handle nested settings structure
    if (setting.includes('.')) {
      const [category, key] = setting.split('.');
      newSettings = {
        ...currentSettings,
        [category]: {
          ...currentSettings[category],
          [key]: value
        }
      };
    } else {
      // Handle frequency settings that are at the root level
      if (setting === 'digestFrequency') {
        newSettings = {
          ...currentSettings,
          frequency: {
            ...currentSettings.frequency,
            digestFrequency: value
          }
        };
      } else {
        // Handle email settings (legacy support)
        newSettings = {
          ...currentSettings,
          email: {
            ...currentSettings.email,
            [setting]: value
          }
        };
      }
    }
    
    // Update main profile data through the parent component's handler
    onInputChange('notification_settings', newSettings);
  };

  // Get email settings for component rendering
  const emailSettings = notificationSettings.email || {};

  return (
    <div className="space-y-6">
      {/* Email Notifications */}
      <SettingsSection
        title="Email Notifications"
        description="Manage when and how you receive email notifications"
        icon="Mail"
      >
        <div className="space-y-4">
          <SettingItem
            title="New Applications"
            description="Get notified when candidates apply for your job posts"
            control={
              <ToggleSwitch
                checked={emailSettings.newApplications}
                onChange={(checked) => handleToggleChange('newApplications', checked)}
              />
            }
          />

          <SettingItem
            title="Messages"
            description="New messages from candidates and support"
            control={
              <ToggleSwitch
                checked={emailSettings.messages}
                onChange={(checked) => handleToggleChange('messages', checked)}
              />
            }
          />

          <SettingItem
            title="Candidate Matches"
            description="Notifications about candidates that match your job requirements"
            control={
              <ToggleSwitch
                checked={emailSettings.candidateMatches}
                onChange={(checked) => handleToggleChange('candidateMatches', checked)}
              />
            }
          />

          <SettingItem
            title="Payment Updates"
            description="Payment confirmations and billing notifications"
            control={
              <ToggleSwitch
                checked={emailSettings.paymentUpdates}
                onChange={(checked) => handleToggleChange('paymentUpdates', checked)}
              />
            }
          />

          <SettingItem
            title="Project Updates"
            description="Status changes and milestones for your active projects"
            control={
              <ToggleSwitch
                checked={emailSettings.projectUpdates}
                onChange={(checked) => handleToggleChange('projectUpdates', checked)}
              />
            }
          />

          <SettingItem
            title="Contract Reminders"
            description="Reminders about contract renewals and expirations"
            control={
              <ToggleSwitch
                checked={emailSettings.contractReminders}
                onChange={(checked) => handleToggleChange('contractReminders', checked)}
              />
            }
          />

          <SettingItem
            title="Interview Reminders"
            description="Reminders for scheduled interviews with candidates"
            control={
              <ToggleSwitch
                checked={emailSettings.interviewReminders}
                onChange={(checked) => handleToggleChange('interviewReminders', checked)}
              />
            }
          />

          <SettingItem
            title="Security Alerts"
            description="Important security notifications about your account"
            control={
              <ToggleSwitch
                checked={emailSettings.securityAlerts}
                onChange={(checked) => handleToggleChange('securityAlerts', checked)}
              />
            }
          />

          <SettingItem
            title="Marketing Emails"
            description="Platform updates, hiring tips, and promotional content"
            control={
              <ToggleSwitch
                checked={emailSettings.marketingEmails}
                onChange={(checked) => handleToggleChange('marketingEmails', checked)}
              />
            }
          />

          <SettingItem
            title="System Updates"
            description="Platform maintenance and system announcements"
            control={
              <ToggleSwitch
                checked={emailSettings.systemUpdates}
                onChange={(checked) => handleToggleChange('systemUpdates', checked)}
              />
            }
          />
        </div>
      </SettingsSection>

      {/* Push Notifications */}
      <SettingsSection
        title="Push Notifications"
        description="Real-time notifications in your browser"
        icon="Bell"
      >
        <div className="space-y-4">
          <SettingItem
            title="Messages"
            description="Instant notifications for new messages"
            control={
              <ToggleSwitch
                checked={notificationSettings.push?.messages}
                onChange={(checked) => handleToggleChange('push.messages', checked)}
              />
            }
          />

          <SettingItem
            title="New Applications"
            description="Real-time alerts for new job applications"
            control={
              <ToggleSwitch
                checked={notificationSettings.push?.newApplications}
                onChange={(checked) => handleToggleChange('push.newApplications', checked)}
              />
            }
          />

          <SettingItem
            title="Urgent Updates"
            description="Critical notifications requiring immediate attention"
            control={
              <ToggleSwitch
                checked={notificationSettings.push?.urgentUpdates}
                onChange={(checked) => handleToggleChange('push.urgentUpdates', checked)}
              />
            }
          />
        </div>
      </SettingsSection>

      {/* Email Frequency */}
      <SettingsSection
        title="Email Frequency"
        description="Control how often you receive digest emails"
        icon="Clock"
      >
        <div className="space-y-4">
          <SettingItem
            title="Digest Frequency"
            description="How often to receive summary emails"
            control={
              <select
                value={notificationSettings.frequency?.digestFrequency || 'daily'}
                onChange={(e) => handleToggleChange('digestFrequency', e.target.value)}
                className="px-3 py-2 border border-border rounded-md bg-surface focus:ring-2 focus:ring-primary focus:border-transparent"
              >
                <option value="immediate">Immediate</option>
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="never">Never</option>
              </select>
            }
          />
        </div>
      </SettingsSection>

      {/* Information Section */}
      <div className="bg-surface border border-border rounded-lg p-6">
        <div className="flex items-start space-x-3">
          <Icon name="Info" size={20} className="text-primary flex-shrink-0 mt-0.5" />
          <div className="space-y-2">
            <h4 className="font-medium text-text-primary">Email Delivery & Privacy</h4>
            <div className="text-sm text-text-secondary space-y-2">
              <p>
                All email notifications are sent from our secure servers and include unsubscribe options.
                We never share your email with third parties.
              </p>
              <ul className="list-disc list-inside space-y-1 pl-4">
                <li>Security alerts cannot be disabled for account safety</li>
                <li>Critical hiring notifications are always sent regardless of settings</li>
                <li>You can update these preferences anytime</li>
                <li>Changes take effect immediately</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnhancedNotificationsTab;